<template>
    <div
      class="create-job-description-container flex flex-col h-screen max-w-4xl mx-auto p-4 bg-white shadow-md rounded-md relative"
    >
      <!-- Modal de Verificación de Código -->
      <div
        v-if="showCodeModal"
        class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center"
      >
        <div class="bg-white p-8 rounded-md shadow-lg max-w-md w-full">
          <h2 class="text-2xl font-semibold mb-4 text-center">
            Ingresa tu código de acceso
          </h2>
          <div class="mb-4">
            <input
              v-model="accessCode"
              type="text"
              placeholder="Código de acceso"
              class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-300"
              @keyup.enter="verifyAccessCode"
              required
            />
          </div>
          <div class="flex justify-center">
            <button
              @click="verifyAccessCode"
              class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              :disabled="loading"
            >
              <span v-if="loading">Verificando...</span>
              <span v-else>Verificar</span>
            </button>
          </div>
          <div v-if="codeError" class="text-red-500 mt-2 text-center">{{
            codeError
          }}</div>
        </div>
      </div>
      <div v-else>
          <div class="mb-4 flex justify-between items-center">
              <router-link to="/dashboard" class="no-underline text-blue-600">
              <h1 class="logo-text-chat">interbot.mx</h1>
              </router-link>
              <button @click="clearAccessCode" class="text-blue-600 hover:text-blue-800 focus:outline-none text-sm">
                  Cambiar Código
                </button>
          </div>
        <h2 class="text-2xl font-semibold mb-4 text-center">
          Asistente de creación de descripciones de puesto
        </h2>
        <div class="flex flex-col flex-1 justify-start">
          <div
            id="chat-history"
            ref="chatHistoryDiv"
            class="border border-gray-300 rounded p-2 overflow-y-auto flex-1"
            :style="{ maxHeight: chatMaxHeight, minHeight: '300px' }"
          >
            <ChatMessage
              v-for="(message, index) in chatHistory"
              :key="index"
              :message="message"
            />
          </div>
          <div
            v-if="isChatActive"
            class="flex items-center p-2 bg-gray-100 shadow-inner"
            ref="inputAreaContainer"
          >
            <textarea
              v-model="newMessage"
              placeholder="Escribe tu solicitud..."
              required
              @keyup.enter.prevent="sendMessage"
              class="flex-1 border rounded p-2 mr-2 focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none overflow-y-auto"
              :style="{ maxHeight: '150px', paddingLeft: '10px' }"
            ></textarea>
            <button
              type="button"
              @click="sendMessage"
              class="p-2 rounded-full bg-blue-500 hover:bg-blue-600 text-white focus:outline-none flex items-center justify-center"
              :disabled="loading"
            >
              <span v-if="loading">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="feather feather-hourglass"
                >
                  <path d="M6 2l6 8 6-8M6 22l6-8 6 8" />
                  <path d="M2 12h20" />
                </svg>
              </span>
              <span v-else>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="feather feather-send"
                >
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </span>
            </button>
          </div>
           <div v-else class="text-center p-2 bg-gray-100 shadow-inner">
              <p class="text-lg font-semibold text-gray-700">
                  Descripción de puesto concluida.
              </p>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import ChatMessage from "@/components/ChatMessage.vue";
  import axios from "axios";
  
  export default {
    name: "CreateJobDescriptionView",
    components: {
      ChatMessage,
    },
    data() {
      return {
        showCodeModal: true,
        accessCode: "",
        codeError: null,
        newMessage: "",
        chatHistory: [],
        loading: false,
        chatHistoryDiv: null,
        windowHeight: window.innerHeight,
        inputAreaHeight: 50,
        initialChatMaxHeight: 0,
        isChatActive: true, // Inicialmente la conversación está activa
      };
    },
    computed: {
      chatMaxHeight() {
        return `${
          this.windowHeight - (this.initialChatMaxHeight + this.inputAreaHeight)
        }px`;
      },
    },
    mounted() {
      this.calculateInitialChatMaxHeight();
      window.addEventListener("resize", this.handleResize);
      this.$nextTick(() => {
        this.calculateInputAreaHeight();
      });
    },
    async beforeMount() {
      // Intenta recuperar el accessCode del localStorage al cargar la página, si no existe, no hace nada
      const storedAccessCode = localStorage.getItem("accessCode");
      if (storedAccessCode) {
        this.accessCode = storedAccessCode;
        await this.loadChatHistory();
        this.showCodeModal = false;
      }
    },
    beforeUnmount() {
      window.removeEventListener("resize", this.handleResize);
    },
    methods: {
      calculateInitialChatMaxHeight() {
        this.initialChatMaxHeight = 180;
      },
      handleResize() {
        this.windowHeight = window.innerHeight;
        this.calculateInputAreaHeight();
      },
      calculateInputAreaHeight() {
        if (this.$refs.inputAreaContainer) {
          this.inputAreaHeight = this.$refs.inputAreaContainer.offsetHeight;
        }
      },
      formatTime(timestamp) {
        const date = new Date(timestamp);
        return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      },
      async verifyAccessCode() {
        this.loading = true;
        this.codeError = null;
  
        try {
          const response = await axios.post("./api/verify-job-code", {
            accessCode: this.accessCode,
          });
          if (response.status !== 200) {
            this.codeError = "Código inválido, por favor verifica";
          } else {
            localStorage.setItem("accessCode", this.accessCode);
            await this.loadChatHistory();
            this.showCodeModal = false;
          }
        } catch (error) {
          console.error("Error during access code verification:", error);
          this.codeError =
            "Ocurrió un error al verificar el código, por favor intenta de nuevo";
        } finally {
          this.loading = false;
        }
      },
      async loadChatHistory() {
        try {
          const response = await axios.get(
            `./api/get-job-description-chat/${this.accessCode}`
          );
          if (response.status === 200) {
            this.chatHistory = response.data.chatHistory;
            this.isChatActive = response.data.isChatActive !== false; // Inicializar isChatActive
            this.$nextTick(() => {
              if (this.$refs.chatHistoryDiv) {
                this.$refs.chatHistoryDiv.scrollTop =
                  this.$refs.chatHistoryDiv.scrollHeight;
              }
            });
          } else {
            console.error("Error loading chat history:", response);
          }
        } catch (error) {
          console.error("Error loading chat history:", error);
        }
      },
      async sendMessage() {
        if (this.newMessage.trim() !== "") {
          this.loading = true;
          const userMessage = {
            text: this.newMessage,
            type: "user",
            timestamp: new Date().toISOString(),
          };
          this.chatHistory.push(userMessage);
          this.newMessage = "";
          this.scrollToBottom();
          try {
            const response = await axios.post(
              "./api/create-job-description",
              {
                message: userMessage,
              },
              {
                headers: {
                  "access-code": this.accessCode,
                },
              }
            );
            if (response.status !== 200) {
              const errorData = await response.data;
              throw new Error(
                `HTTP error! status: ${response.status} ${JSON.stringify(
                  errorData
                )}`
              );
            }
            const botMessage = {
              text: response.data.message,
              type: "system",
              timestamp: new Date().toISOString(),
            };
            this.chatHistory.push(botMessage);
            this.isChatActive = response.data.isChatActive;
            this.scrollToBottom();
          } catch (error) {
            console.error("Error:", error);
            this.handleApiError(error);
          } finally {
            this.loading = false;
          }
        }
      },
      handleApiError(error) {
        console.error("API Error:", error);
        alert(
          "There was an error connecting to the service, please try again. Check the console for more details."
        );
      },
      scrollToBottom() {
        this.$nextTick(() => {
          if (this.$refs.chatHistoryDiv) {
            this.$refs.chatHistoryDiv.scrollTop =
              this.$refs.chatHistoryDiv.scrollHeight;
          }
        });
      },
      clearAccessCode() {
        localStorage.removeItem("accessCode");
        this.showCodeModal = true;
        this.isChatActive = true;
        this.chatHistory = [];
        this.accessCode = "";
      },
    },
  };
  </script>
  <style scoped>
  .create-job-description-container {
    font-family: "Roboto", sans-serif;
  }
  .logo-text-chat {
    font-family: "Arial", sans-serif;
    font-size: 2em;
    font-weight: bold;
    color: #007bff;
    text-decoration: none;
  }
  </style>